import type { PetPlanParseResult, PetPlanRecord } from '../petPlanTypes';
import { loadXlsx } from './parserUtils';

type HeaderIndexes = {
    name: number;
    email: number;
    pets: number;
    amount: number;
};

const normalizeHeaderValue = (value: unknown): string => {
    if (typeof value !== 'string') return '';
    return value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
};

const findHeaderIndex = (row: (string | number | null)[], keywords: string[]): number => {
    return row.findIndex((cell) => {
        const normalized = normalizeHeaderValue(cell);
        if (!normalized) return false;
        return keywords.some((keyword) => normalized.includes(keyword));
    });
};

const resolveHeaderIndexes = (rows: (string | number | null)[][]): { header: HeaderIndexes; rowIndex: number } => {
    for (let index = 0; index < Math.min(rows.length, 10); index += 1) {
        const row = rows[index];
        if (!row || row.length === 0) continue;
        const header = {
            name: findHeaderIndex(row, ['nome']),
            email: findHeaderIndex(row, ['email', 'e-mail']),
            pets: findHeaderIndex(row, ['quantidade de pets', 'qtd de pets', 'pets']),
            amount: findHeaderIndex(row, ['valor a ser descontado', 'valor descontado', 'valor']),
        };
        const hasAll = Object.values(header).every((value) => value >= 0);
        if (hasAll) {
            return { header, rowIndex: index };
        }
    }
    throw new Error('Cabeçalho não encontrado. Confirme se o layout segue o modelo da Galpeco.');
};

const toNumber = (value: unknown): number | null => {
    if (value == null) return null;
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;
        const hasComma = trimmed.includes(',');
        const sanitized = hasComma ? trimmed.replace(/\./g, '').replace(',', '.') : trimmed;
        const parsed = Number.parseFloat(sanitized);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const toStringValue = (value: unknown): string => {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number') return value.toString();
    return '';
};

export async function parseGuapecoFile(file: File): Promise<PetPlanParseResult> {
    const XLSX = await loadXlsx();
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
        throw new Error('Arquivo inválido. Aba principal não encontrada.');
    }
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) {
        throw new Error('Arquivo inválido. Conteúdo da aba não encontrado.');
    }

    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }) as (string | number | null)[][];
    if (!rows.length) {
        throw new Error('Arquivo vazio.');
    }

    const { header, rowIndex } = resolveHeaderIndexes(rows);
    const records: PetPlanRecord[] = [];

    for (let i = rowIndex + 1; i < rows.length; i += 1) {
        const row = rows[i];
        if (!row || row.length === 0) continue;
        const isEmpty = row.every((cell) => toStringValue(cell) === '');
        if (isEmpty) continue;
        const name = toStringValue(row[header.name]);
        const email = toStringValue(row[header.email]);
        const petsCount = toNumber(row[header.pets]);
        const amountValue = toNumber(row[header.amount]);

        if (!name || !email || amountValue == null) continue;

        const dependentsCount = petsCount ?? 0;
        const premiumValue = amountValue ?? 0;

        records.push({
            code: email,
            name,
            dependentsCount,
            feeCount: dependentsCount,
            copay: 0,
            premium: premiumValue,
            amount: premiumValue,
        });
    }

    if (!records.length) {
        throw new Error('Nenhum registro válido foi encontrado no arquivo.');
    }

    const totals = records.reduce(
        (acc, record) => ({
            dependents: acc.dependents + record.dependentsCount,
            feeCount: acc.feeCount + record.feeCount,
            amount: acc.amount + record.amount,
        }),
        { dependents: 0, feeCount: 0, amount: 0 },
    );

    return {
        records,
        totals,
    };
}
